// One place that turns a failed API response into a sentence a business user can read (WK-204).
//
// Two things used to leak through to the UI. A permission failure came back as RFC ProblemDetails,
// whose content-type (application/problem+json) is not matched by an includes('application/json')
// test, so the whole JSON blob was toasted verbatim. And DevExtreme's store, which does parse
// ProblemDetails, hands the grid its `title` — which is only the status name, so grid error rows
// read "Bad Request" / "Unauthorized".
//
// The API now sends real messages of its own (see SquireRequestResultProcessor); this module is what
// covers everything we don't control — the bodyless 401 from the auth middleware, model-binding
// failures, and anything else that answers with a bare status.

const GENERIC_MESSAGE = 'Something went wrong completing that request. Please try again.';

// 403 and 404 are worded identically to SquireRequestResultProcessor so the user hears one voice
// whether the sentence came from the API or from here.
const STATUS_MESSAGES = {
  400: GENERIC_MESSAGE,
  401: 'Your session has expired. Refresh the page to sign in again.',
  403: 'You do not have permission to access this part of Squire. If you need access, contact your administrator.',
  404: 'That record could not be found. It may have been deleted, or you may not have access to it.',
  500: 'Something went wrong on the Squire server. Please try again, and let support know if it keeps happening.',
};

// The status names ASP.NET puts in ProblemDetails.title. Seeing one of these means the response
// carried no real message, so we substitute our own rather than pass the word along.
const STATUS_BY_BARE_NAME = {
  'bad request': 400,
  unauthorized: 401,
  forbidden: 403,
  'not found': 404,
  'internal server error': 500,
};

export function resolveApiErrorMessage({ status, body }) {
  const payload = asObject(body);

  if (payload) {
    const supplied = firstRealMessage(payload.errorMessage, payload.detail, payload.title);
    if (supplied) {
      return supplied;
    }
  } else {
    const supplied = firstRealMessage(body);
    if (supplied) {
      return supplied;
    }
  }

  return describeStatus(status);
}

export function rewriteBareHttpStatusMessage(message) {
  return firstRealMessage(message) ?? describeStatus(STATUS_BY_BARE_NAME[normalize(message)]);
}

function describeStatus(status) {
  return STATUS_MESSAGES[status] ?? GENERIC_MESSAGE;
}

// A candidate only counts if it is a non-blank string that isn't just an HTTP status name.
function firstRealMessage(...candidates) {
  return candidates.find(
    (candidate) =>
      typeof candidate === 'string' &&
      candidate.trim() &&
      !(normalize(candidate) in STATUS_BY_BARE_NAME)
  );
}

function normalize(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

// Bodies reach us either already parsed or still as text, depending on how the caller read the
// response. Anything that isn't an object — a plain-text message, a number — is handled as text.
function asObject(body) {
  if (body && typeof body === 'object') {
    return body;
  }

  if (typeof body !== 'string') {
    return undefined;
  }

  try {
    const parsed = JSON.parse(body);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}
